import { z } from "zod";

/**
 * Parses an object field by field, applying transforms where possible.
 * Fields that fail validation keep their raw value, while valid fields
 * get properly parsed and transformed.
 *
 * @param shape - The shape object from a ZodObject schema (schema.shape)
 * @param data - The raw data object to parse
 * @param context - Optional context string for debugging
 * @returns Object with parsed fields where possible, raw values where validation failed
 */
const parseObjectPartial = <T extends z.ZodRawShape>(
  shape: T,
  data: Record<string, unknown>,
  context?: string
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  const errors: Array<{ field: string; error: z.ZodError }> = [];

  for (const [key, fieldSchema] of Object.entries(shape)) {
    const fieldValue = data[key];
    const fieldResult = fieldSchema.safeParse(fieldValue);

    if (fieldResult.success) {
      result[key] = fieldResult.data;
    } else {
      result[key] = fieldValue; // Keep raw value
      errors.push({ field: key, error: fieldResult.error });
    }
  }

  if (errors.length > 0) {
    // eslint-disable-next-line no-console
    console.error(
      `[Zod Partial Validation Error]${context ? ` ${context}` : ""}:`,
      errors.map((e) => ({
        field: e.field,
        issues: e.error.issues.map((i) => i.message),
      }))
    );
  }

  return result;
};

/**
 * Safely parses data field by field, applying transforms where validation passes.
 * Unlike safeParseWithLogging, this function attempts to parse each field individually,
 * so valid fields get their transforms applied even if other fields fail.
 *
 * Works with:
 * - ZodObject: parses each field individually
 * - ZodArray of ZodObject: parses each array item's fields individually
 *
 * @param schema - Zod schema to validate against (ZodObject or ZodArray of ZodObject)
 * @param data - Data to validate
 * @param context - Optional context string for debugging
 * @returns Partially parsed data with transforms applied where possible
 *
 * @example
 *  If a value expects string but receives number, only that field fails
 *  Other fields still get their dayjs transform applied
 * const items = safeParsePartial(itemArraySchema, apiResponse, "useItems");
 */
export const safeParsePartial = <Output, Input = Output>(
  schema: z.ZodType<Output, z.ZodTypeDef, Input>,
  data: unknown,
  context?: string
): Output => {
  // First try normal parse - if it succeeds, return normally
  const fullResult = schema.safeParse(data);
  if (fullResult.success) {
    return fullResult.data;
  }

  // Handle ZodArray of ZodObject
  if (schema instanceof z.ZodArray) {
    const innerSchema = schema.element;

    if (innerSchema instanceof z.ZodObject && Array.isArray(data)) {
      const shape = innerSchema.shape;
      const parsedArray = data.map((item, index) => {
        if (typeof item === "object" && item !== null) {
          return parseObjectPartial(
            shape,
            item as Record<string, unknown>,
            `${context}[${index}]`
          );
        }
        return item;
      });
      return parsedArray as Output;
    }
  }

  // Handle ZodObject directly
  if (schema instanceof z.ZodObject) {
    if (typeof data === "object" && data !== null) {
      return parseObjectPartial(
        schema.shape,
        data as Record<string, unknown>,
        context
      ) as Output;
    }
  }

  // Fallback: log error and return raw data
  // eslint-disable-next-line no-console
  console.error(
    `[Zod Validation Error]${context ? ` ${context}` : ""}:`,
    fullResult.error.format()
  );
  return data as Output;
};
